import { Inherited, isInherited, TitleFull as Title, titlePaths } from '@warehouse/title/core';
import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { DropdownV2 } from '@warehouse/shared/ui';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import useLocalizedInfosDropdown from '../../../../src/views/title/tab/productMetadata/localizedInfo/useLocalizedInfosDropdown';
import { findDefaultUuidForKey, fromBasic } from '../../../../src/utils/titleGetProperty';
import sortAlphabeticallyByLabel from '../../../../src/utils/sortAlphabeticallyByLabel';
import useTitleAutoSave from '../../../../src/views/title/hooks/useTitleAutoSave/useTitleAutoSave';
import TitleEditableField from '../../../../src/components/titles/TitleEditableField';

export function DefaultLocalizedInfo() {
	const title = useTitleEditorStore(titleEditorStoreSelector.title);
	const changesAppliedRef = useRef(true);
	const localizedInfosDropdown = useLocalizedInfosDropdown(title);
	const defaultLocalizedInfoUuid = findDefaultUuidForKey(title as Title, 'localizedInfos');
	const memoizedPath = useMemo(
		() => fromBasic(['localizedInfos', localizedInfosDropdown.selected || '', 'default']),
		[localizedInfosDropdown.selected],
	);
	const { setValue, value, commit } = useTitleAutoSave<Inherited<boolean>>({
		label: 'Default Localized Info',
		path: memoizedPath,
		shouldDistinctUntilChanged: false,
	});

	const sortedOptions = useMemo(
		() => localizedInfosDropdown.options.sort(sortAlphabeticallyByLabel),
		[localizedInfosDropdown.options],
	);

	const onSelect = async (selectedUuid: string | null) => {
		if (selectedUuid === defaultLocalizedInfoUuid) return;
		if (!selectedUuid) return;
		if (!title) return;

		localizedInfosDropdown.setSelected(selectedUuid || undefined);
		changesAppliedRef.current = false;
	};

	const isInheritedResult = isInherited({ inheritedObject: value }).isInherited;

	useEffect(() => {
		if (changesAppliedRef.current) return;
		setValue((prev) => ({ ...prev, displayValue: true }));
		commit({ force: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localizedInfosDropdown.selected]);

	return useMemo(
		() =>
			defaultLocalizedInfoUuid ? (
				<FieldWrapper>
					<TitleEditableField
						label="Default Localized Info"
						path={titlePaths.defaultLocalizedInfo(defaultLocalizedInfoUuid)}
						direction="column"
						readComponent={undefined}
						editComponent={undefined}
					/>
					<DropdownV2
						height={38}
						ariaLabel="Default Localized Info"
						variant={isInheritedResult ? 'prefilled' : 'default'}
						disabledMargin
						withSearch
						options={sortedOptions || []}
						value={localizedInfosDropdown.selected || null}
						onChange={onSelect}
					/>
				</FieldWrapper>
			) : null,
		// We can't set all dependencies, otherwise the component will be rendered infinitely after the first change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[localizedInfosDropdown.selected, isInheritedResult, defaultLocalizedInfoUuid, sortedOptions],
	);
}

const FieldWrapper = styled.div(
	({ theme }) => `
		display: flex;
		flex-direction: column;
		gap: ${theme.spacing(1)};
		svg {
			color: ${theme.palette.text.secondary};
		}
		margin-bottom: 14px;
		
				.title-editable-field {
			padding: 0;
		}
	`,
);
